import { Component, inject, OnDestroy } from '@angular/core';
import { AnomalySeverity } from '@buda/app/types/anomaly.types';
import { MobileService } from '@common-services/ui/mobile.service';
import { AclService } from '@common-services/util/acl.service';
import { NotificationsUtilService } from '@common-services/util/notifications-util.service';
import { NotificationsState } from '@dice/app/store/notifications/notifications.state';
import { ProfileState } from '@dice/app/store/profile/profile.state';
import {
  ProfileNotification,
  ProfileNotificationAnomalyContent,
  ProfileNotificationType,
} from '@dice/app/types/notification.types';
import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';
import { StateService } from '@uirouter/angular';
import { has as _has } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'profile-header',
  templateUrl: './profile-header.component.html',
  standalone: false,
})
export class ProfileHeaderComponent implements OnDestroy {
  mobileService = inject(MobileService);
  private translateService = inject(TranslateService);
  private notificationsUtilService = inject(NotificationsUtilService);
  private stateService = inject(StateService);
  private aclService = inject(AclService);

  @Select(NotificationsState.allUserNotifications) allNotifications$: Observable<ProfileNotification[]>;

  @Select(ProfileState.fakeMode) fakeMode$: Observable<boolean>;

  readonly notificationToDisplay$: BehaviorSubject<ProfileNotification> = new BehaviorSubject<ProfileNotification>(
    null,
  );

  private readonly unsubscriber: Subject<void> = new Subject<void>();

  private readonly eskimoStatusPageUrl: string;

  constructor() {
    this.allNotifications$.pipe(takeUntil(this.unsubscriber)).subscribe((allNotifications: ProfileNotification[]) => {
      const majorNotification: ProfileNotification = allNotifications.find((notification: ProfileNotification) => {
        const { type, content } = notification;
        return (
          type === ProfileNotificationType.ANOMALY &&
          !!(content as ProfileNotificationAnomalyContent).severity &&
          (content as ProfileNotificationAnomalyContent).severity === AnomalySeverity.CRITICAL
        );
      });
      this.notificationToDisplay$.next(majorNotification);
    });
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
  }

  get severity(): AnomalySeverity {
    const notification: ProfileNotification = this.notificationToDisplay$.getValue();
    if (_has(notification, 'content.severity')) {
      const { severity }: ProfileNotificationAnomalyContent = notification.content as ProfileNotificationAnomalyContent;
      return severity;
    }
    return null;
  }

  get area(): string {
    const notification: ProfileNotification = this.notificationToDisplay$.getValue();
    if (_has(notification, 'content.area')) {
      const { area } = notification.content as ProfileNotificationAnomalyContent;
      return area;
    }
    return null;
  }

  get notificationContent(): string {
    return this.area ?? '';
  }

  navigateTo(): void {
    const notification: ProfileNotification = this.notificationToDisplay$.getValue();
    this.notificationsUtilService.navigateToNotificationContent(notification);
  }
}
